/**
 * Helpers de presentación / derivación del Cotizador de puertas.
 *
 * Puro formato y derivación de UI; sin lógica remota ni acceso a la API
 * (`docs/frontend_architecture.md`, Pr. 6) y sin tocar el motor de cálculo. Replican el
 * formato del SSR (`cotizador_puertas.html`): dinero en es-CO, clasificación de líneas del
 * ticket técnico (`renderTicketTechnical`), etiqueta de modelo y medidas del historial.
 */
import { ApiError } from "@/lib/api";

import { COTIZADOR_GENERIC_ERROR, MODELO_LABELS } from "./constants";
import type { CotizacionHistory, DoorMeasure } from "./types";

/**
 * Traduce un error de la API/red a un mensaje legible. La validación del backend
 * (`{error_code, message}`: "Medidas inválidas", "Total del cotizador inválido"…) ya viene
 * en español; se usa el `message` del `ApiError` o del `Error`, con un texto de respaldo.
 */
export function cotizadorErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return COTIZADOR_GENERIC_ERROR;
}

/** Formatea un valor a pesos colombianos sin decimales (réplica del `toLocaleString` SSR). */
export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "$0";
  }
  return `$${Number(value).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;
}

/** Clase semántica de una línea del ticket técnico (gobierna su color/peso en la UI). */
export type TicketLineKind =
  | "plain"
  | "sec-a"
  | "sec-b"
  | "subtotal"
  | "total"
  | "sep"
  | "dim";

/** Una línea ya clasificada del ticket técnico (texto + tipo). */
export interface TicketLine {
  text: string;
  kind: TicketLineKind;
}

/**
 * Clasifica cada línea del ticket de texto del motor en su tipo de presentación, réplica de
 * `renderTicketTechnical()` del SSR (que devolvía HTML con clases `ticket-*`). Aquí se
 * devuelve la lista tipada para que la pantalla la renderice sin `dangerouslySetInnerHTML`
 * (más seguro y testeable). El texto NO se altera: solo se etiqueta.
 */
export function parseTicket(text: string | null | undefined): TicketLine[] {
  if (text === null || text === undefined || text === "") {
    return [];
  }
  return String(text)
    .split("\n")
    .map((line) => {
      const t = line.trim();
      let kind: TicketLineKind = "plain";
      if (/^\[A\]/.test(t)) {
        kind = "sec-a";
      } else if (/^\[B\]/.test(t)) {
        kind = "sec-b";
      } else if (/SUBTOTAL|SUB TOTAL/i.test(t)) {
        kind = "subtotal";
      } else if ((/^=+$/.test(t) && t.length >= 8) || /^-{3,}$/.test(t)) {
        kind = "sep";
      } else if (
        /TOTAL\s+GENERAL|GASTO\s+/i.test(t) ||
        (/^\s*TOTAL[\s:]/i.test(t) && /\$/.test(t))
      ) {
        kind = "total";
      } else if (/^\[/.test(t)) {
        // Otras secciones entre corchetes: línea de dimensión/detalle.
        kind = "dim";
      }
      return { text: line, kind };
    });
}

/** Etiqueta corta de un modelo por id (réplica de `modeloLabel` del historial SSR). */
export function modeloLabel(modeloId: string | null | undefined): string {
  if (!modeloId) {
    return "—";
  }
  return MODELO_LABELS[String(modeloId)] ?? `Modelo ${modeloId}`;
}

/**
 * Formatea las medidas de una cotización del historial (réplica de `fmtMedidas` del SSR):
 * un objeto -> "AxH"; un array -> "AxH (N uds)" con la primera medida y el conteo.
 */
export function formatHistoryMedidas(item: CotizacionHistory): string {
  const { medidas } = item;
  if (!medidas) {
    return "—";
  }
  if (Array.isArray(medidas)) {
    const first = medidas[0] as DoorMeasure | undefined;
    if (!first) {
      return "—";
    }
    const count = item.cantidad || medidas.length;
    return `${first.ancho}x${first.alto} (${count} uds)`;
  }
  return `${medidas.ancho}x${medidas.alto} (${item.cantidad || 1} uds)`;
}

/** Fecha del historial formateada en locale (réplica de `toLocaleString` del SSR). */
export function formatHistoryDate(createdAt: string | null): string {
  if (!createdAt) {
    return "—";
  }
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("es-CO");
}

/** Porcentaje formateado con un decimal (para las stats de error del historial). */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(1)}%`;
}
